import Axios from 'axios';

import {
    getArtDocumentSource,
    getArtMediaDocumentId,
    normalizeArtMediaType,
    resolveArtDocumentFileUrl,
} from '@containers/Art/ArtDocuments';
import {
    GALLERY_MEDIA_TYPE,
} from '@containers/Art/Galleries/Galleries.constants';

export const GALLERIES_SAVE_MODE = {
    API: 'api',
};

const GALLERIES_API_BASE = '/cms/galleries';

const makeLocalID = () => {
    return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const normalizeName = (value) => {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ');
};

const getSafePosition = (position, fallbackPosition) => {
    const numericPosition = Number(position);
    return Number.isFinite(numericPosition) ? numericPosition : fallbackPosition;
};

const VIDEO_FILE_EXTENSIONS = ['.mp4', '.mov', '.m4v', '.webm', '.ogv'];
/** Gallery APIs expose image and video document variants in the same Media array. */
const GALLERY_THUMBNAIL_VARIANT_TYPES = ['Thumbnail', 'Cover', 'LinkedCover'];
const GALLERY_STANDARD_VARIANT_TYPES = ['Standard', 'Video', 'LinkedVideo', 'File'];
const GALLERY_VIDEO_VARIANT_TYPES = ['Video', 'LinkedVideo'];

const normalizeGalleryMediaVariant = (mediaVariant = {}) => {
    const url = resolveArtDocumentFileUrl(mediaVariant.Url || mediaVariant.url);

    return {
        ...mediaVariant,
        ...(url ? { Url: url } : {}),
    };
};

const normalizeGalleryDocumentVariant = (mediaVariant, variantType) => {
    if (!mediaVariant) return undefined;
    const normalizedVariant = normalizeGalleryMediaVariant({
        ...mediaVariant,
        Type: mediaVariant.Type || mediaVariant.type || variantType,
    });
    return normalizedVariant.Url ? normalizedVariant : undefined;
};

const getGalleryMediaVariants = (media = {}) => {
    const variants = media.Media || media.media || [];
    if (!Array.isArray(variants)) return [];
    return variants.map(normalizeGalleryMediaVariant);
};

const getGalleryMediaVariant = (variants = [], variantTypes = []) => {
    const normalizedVariantTypes = variantTypes.map((variantType) => variantType.toLowerCase());

    return variants.find((variant) =>
        normalizedVariantTypes.includes(String(variant.Type || variant.type || '').toLowerCase())
    );
};

const inferGalleryMediaType = (media = {}, standardVariant) => {
    const normalizedType = normalizeArtMediaType(
        media.Type ??
            media.type ??
            media.MediaType ??
            media.mediaType ??
            media.TypeName ??
            media.typeName ??
            media.DocumentType ??
            media.documentType
    );
    if (normalizedType) return normalizedType;

    const standardVariantType = String(standardVariant?.Type || standardVariant?.type || '').toLowerCase();
    if (GALLERY_VIDEO_VARIANT_TYPES.some((variantType) => variantType.toLowerCase() === standardVariantType)) {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }

    const standardUrl = String(standardVariant?.Url || '').toLowerCase();
    if (VIDEO_FILE_EXTENSIONS.some((extension) => standardUrl.includes(extension))) {
        return GALLERY_MEDIA_TYPE.VIDEO;
    }

    return GALLERY_MEDIA_TYPE.IMAGE;
};

const getGalleryDocumentType = (mediaType) => {
    return mediaType === GALLERY_MEDIA_TYPE.VIDEO ? 'Video' : 'Photo';
};

const normalizeApiResponse = (response) => {
    return response && response.data !== undefined ? response.data : response;
};

const normalizeApiDate = (value) => {
    if (!value || String(value).startsWith('0001-01-01')) return undefined;
    return value;
};

const isApiGalleryId = (id) => {
    const numericId = Number(id);
    return Number.isInteger(numericId) && numericId > 0;
};

export const makeGalleryMediaId = () => {
    return makeLocalID();
};

const normalizeGalleryMediaItem = (media = {}, index = 0) => {
    const legacyDocumentId =
        media.DocumentID ??
        media.documentId ??
        media.MediaId ??
        media.mediaId ??
        media.LocalMediaId ??
        media.localMediaId;
    const mediaVariants = getGalleryMediaVariants(media);
    const thumbnail =
        getGalleryMediaVariant(mediaVariants, GALLERY_THUMBNAIL_VARIANT_TYPES) ||
        normalizeGalleryDocumentVariant(media.Thumbnail || media.thumbnail, 'Thumbnail');
    const standard =
        getGalleryMediaVariant(mediaVariants, GALLERY_STANDARD_VARIANT_TYPES) ||
        normalizeGalleryDocumentVariant(media.Standard || media.standard, 'Standard') ||
        getGalleryMediaVariant(mediaVariants, ['HD']) ||
        normalizeGalleryDocumentVariant(media.HD || media.hd, 'HD');
    const hd =
        getGalleryMediaVariant(mediaVariants, ['HD']) ||
        normalizeGalleryDocumentVariant(media.HD || media.hd, 'HD');
    const mediaType = inferGalleryMediaType(media, standard);
    const normalizedMediaVariants = mediaVariants.length ? mediaVariants : [thumbnail, standard, hd].filter(Boolean);

    return {
        Caption: String(media.Caption || media.caption || '').trim(),
        DocumentID: legacyDocumentId,
        HD: hd,
        ID: media.ID || makeGalleryMediaId(),
        Media: normalizedMediaVariants,
        Position: getSafePosition(media.Position, index + 1),
        Source: media.Source || media.source || getArtDocumentSource(media),
        Standard: standard,
        Thumbnail: thumbnail,
        Type: getGalleryDocumentType(mediaType),
        Url: standard?.Url,
    };
};

const normalizeGalleryMediaItems = (mediaItems = []) => {
    return [...mediaItems]
        .map(normalizeGalleryMediaItem)
        .sort((firstItem, secondItem) => firstItem.Position - secondItem.Position)
        .map((mediaItem, index) => ({
            ...mediaItem,
            Position: index + 1,
        }));
};

const getGalleryMediaItemsSource = (gallery = {}) => {
    if (Array.isArray(gallery.MediaItems) && gallery.MediaItems.length) {
        return gallery.MediaItems;
    }
    if (Array.isArray(gallery.Items)) {
        return gallery.Items;
    }
    return [];
};

/** Normalizes a gallery detail payload from the CMS API. */
const normalizeGalleryDetail = (gallery = {}) => {
    const mediaItems = normalizeGalleryMediaItems(getGalleryMediaItemsSource(gallery));

    return {
        ...gallery,
        ArtContentSortIndex: gallery.ArtContentSortIndex,
        CreatedAt: normalizeApiDate(gallery.CreatedAt),
        Description: String(gallery.Description || '').trim(),
        DocumentsCount: gallery.DocumentsCount ?? mediaItems.length,
        FirstDocumentID: gallery.FirstDocumentID ?? mediaItems[0]?.DocumentID,
        ID: gallery.ID,
        MediaItems: mediaItems,
        Name: normalizeName(gallery.Name || gallery.Title),
        UpdatedAt: normalizeApiDate(gallery.UpdatedAt),
    };
};

/** Normalizes a gallery list item from the CMS API. */
const normalizeGallerySummary = (gallery = {}) => {
    return {
        ...gallery,
        CreatedAt: normalizeApiDate(gallery.CreatedAt),
        Description: String(gallery.Description || '').trim(),
        DocumentsCount: gallery.DocumentsCount ?? 0,
        FirstDocumentThumbnailUrl: gallery.FirstDocumentThumbnailUrl || null,
        ID: gallery.ID,
        MediaItems: [],
        Name: normalizeName(gallery.Name || gallery.Title),
        UpdatedAt: normalizeApiDate(gallery.UpdatedAt),
    };
};

const buildGallerySavePayload = (gallery = {}) => {
    const now = new Date().toISOString();
    const saveItems = normalizeGalleryMediaItems(
        getGalleryMediaItemsSource(gallery).filter(
            (item) => getArtMediaDocumentId(item) && !item.IsUploading
        )
    );

    return {
        CreatedAt: gallery.CreatedAt || now,
        Description: String(gallery.Description || '').trim(),
        ID: isApiGalleryId(gallery.ID) ? Number(gallery.ID) : 0,
        Items: saveItems.map((item) => ({
            Caption: item.Caption,
            DocumentID: item.DocumentID,
            ID: 0,
            Position: item.Position,
        })),
        Name: normalizeName(gallery.Name),
        UpdatedAt: now,
    };
};

export const makeUploadGalleryMediaItem = ({ document, documentId, position }) => {
    const resolvedDocumentId = documentId ?? document?.ID ?? document?.id;

    return normalizeGalleryMediaItem(
        {
            ...(document || {}),
            DocumentID: resolvedDocumentId,
            Position: position,
        },
        position - 1
    );
};

export const makeNetwalkGalleryMediaItem = ({ media, position }) => {
    return normalizeGalleryMediaItem(
        {
            ...(media.original || {}),
            ...media,
            DocumentID: media.ID,
            Caption:
                media.original && media.original.PublicDescription ? media.original.PublicDescription : media.caption,
            Position: position,
        },
        position - 1
    );
};

export const makeEmbedGalleryMediaItem = ({ document, documentId, position }) => {
    const resolvedDocumentId = documentId ?? document?.ID ?? document?.id;

    return normalizeGalleryMediaItem(
        {
            ...(document || {}),
            DocumentID: resolvedDocumentId,
            Position: position,
        },
        position - 1
    );
};

export const hasGalleryMediaDocument = (media = {}) => {
    return Boolean(getArtMediaDocumentId(media));
};

export const loadGalleryById = async (galleryId) => {
    const response = await Axios.get(`${GALLERIES_API_BASE}/${galleryId}`);
    return normalizeGalleryDetail(normalizeApiResponse(response));
};

export const loadGalleriesApi = async () => {
    const response = await Axios.get(GALLERIES_API_BASE);
    const galleries = normalizeApiResponse(response);
    if (!Array.isArray(galleries)) return [];
    return galleries.map((gallery) => normalizeGallerySummary(gallery));
};

export const saveGalleryApi = async (gallery) => {
    const payload = buildGallerySavePayload(gallery);
    let savedId = isApiGalleryId(gallery.ID) ? Number(gallery.ID) : null;

    if (savedId) {
        const response = await Axios.put(`${GALLERIES_API_BASE}/${savedId}`, payload);
        const savedGallery = normalizeApiResponse(response);
        if (savedGallery?.ID || savedGallery?.id) {
            return normalizeGalleryDetail(savedGallery);
        }
    } else {
        const response = await Axios.post(GALLERIES_API_BASE, payload);
        const saved = normalizeApiResponse(response);
        savedId = saved?.ID ?? saved?.id;
    }

    if (!savedId) {
        throw new Error('Invalid gallery save response');
    }

    return loadGalleryById(savedId);
};

export const removeGalleryApi = async (galleryID) => {
    await Axios.delete(`${GALLERIES_API_BASE}/${galleryID}`);
};

export const removeGalleryItemApi = async (galleryID, itemID) => {
    const response = await Axios.delete(`${GALLERIES_API_BASE}/removeItem/${galleryID}/${itemID}`);
    const removedItemGallery = normalizeApiResponse(response);

    if (!removedItemGallery?.ID && !removedItemGallery?.id) {
        throw new Error('Invalid gallery remove item response');
    }

    return normalizeGalleryDetail(removedItemGallery);
};

export const loadGalleries = async () => loadGalleriesApi();

export const saveGallery = async (gallery) => saveGalleryApi(gallery);

export const removeGallery = async (galleryID) => removeGalleryApi(galleryID);

export const removeGalleryItem = async (galleryID, itemID) => removeGalleryItemApi(galleryID, itemID);
